import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { formatDateTimeFull } from '@/utils';
import { ROUTES } from '../../routes.ts';
import { PAGE_SIZE } from './operatorTableConstants.ts';
import { Th, OperatorPagination } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import type { AuditLogListResponse, AuditLogEntry } from '@dermalink/shared';

const EVENT_TYPE_OPTIONS = [
  'auth.login',
  'profile.updated',
  'doctor.verification.identity.attempt',
  'doctor.verification.identity.locked',
  'doctor.verification.riziv.confirmed',
  'doctor.verification.tos.accepted',
  'conversation.created',
  'conversation.payment.paid',
  'conversation.payment.refunded',
  'conversation.closed',
  'conversation.deleted',
  'message.sent',
  'operator.audit_log.viewed',
  'operator.account.suspended',
  'operator.account.reinstated',
  'operator.patients.searched',
  'operator.patient.email_viewed',
] as const;

const DEFAULT_SELECTED = new Set(EVENT_TYPE_OPTIONS.filter((t) => t !== 'operator.audit_log.viewed'));

export function OperatorAuditPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<AuditLogListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [page, setPage]       = useState(1);

  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(DEFAULT_SELECTED);

  const [actorSubInput, setActorSubInput] = useState('');
  const [actorSub, setActorSub]           = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({ page: String(page) });
    if (selectedTypes.size < EVENT_TYPE_OPTIONS.length) {
      for (const t of selectedTypes) params.append('event_type', t);
    }
    if (actorSub) params.set('actor_sub', actorSub.trim());
    authFetch(`/operator/audit-logs?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: AuditLogListResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch, page, selectedTypes, actorSub]);

  useEffect(() => { load(); }, [load]);

  function handleSearch() {
    setActorSub(actorSubInput);
    setPage(1);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSearch();
  }

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className={panel.section}>
      <div className={panel.toolbar} style={{ alignItems: 'flex-start' }}>
        <select
          multiple
          value={[...selectedTypes]}
          onChange={(e) => {
            const selected = new Set(Array.from(e.target.selectedOptions, (o) => o.value));
            setSelectedTypes(selected);
            setPage(1);
          }}
          className={panel.field}
          style={{ fontFamily: 'monospace', minWidth: '260px', height: '180px' }}
        >
          {EVENT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          type="text"
          placeholder="Actor sub (deels)"
          value={actorSubInput}
          onChange={(e) => setActorSubInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className={panel.field}
          style={{ minWidth: '220px' }}
        />
        <Button variant="primary" onClick={handleSearch}>Zoeken</Button>
      </div>

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon auditlog niet laden.</p>}

      {data && !loading && (
        <>
          <div className={panel.resultCount}>{data.total} {data.total === 1 ? 'resultaat' : 'resultaten'} — pagina {page} van {totalPages}</div>
          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>Tijdstip</Th>
                  <Th>Gebeurtenis</Th>
                  <Th>Rol</Th>
                  <Th>Actor sub</Th>
                  <Th>Doel sub</Th>
                  <Th>Bron</Th>
                  <Th>IP</Th>
                  <Th>Metadata</Th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => <AuditRow key={e.id} entry={e} />)}
                {data.entries.length === 0 && (
                  <tr><td colSpan={8} className={panel.emptyCell}>Geen resultaten gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <OperatorPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export function OperatorAuditLog() {
  return (
    <OperatorPageShell maxWidth="100%">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Auditlogboek</h1>
      </div>
      <OperatorAuditPanel />
    </OperatorPageShell>
  );
}

function AuditRow({ entry: e }: { entry: AuditLogEntry }) {
  const ts = formatDateTimeFull(e.created_at);
  return (
    <tr>
      <td className={panel.td} style={{ whiteSpace: 'nowrap', color: 'var(--c-text-muted)' }}>{ts}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{e.event_type}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{e.actor_role}</td>
      <td className={panel.td} style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', color: 'var(--c-text-muted)' }}>{e.actor_sub}</td>
      <td className={panel.td} style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', color: 'var(--c-text-muted)' }}>{e.target_sub ?? '—'}</td>
      <td className={panel.td} style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
        {e.resource_type ? `${e.resource_type}/${e.resource_id}` : '—'}
      </td>
      <td className={panel.td} style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', color: 'var(--c-text-muted)' }}>
        {e.ip_address ?? '—'}
      </td>
      <td className={panel.td} style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
        {e.metadata ? JSON.stringify(e.metadata) : '—'}
      </td>
    </tr>
  );
}
